import { getCore } from '../../../client/core/getCore';
import { getDefaultClient } from '../../../client/defaultClient';

type GetElevatedAccessTokenParams = {
  /**
   * Whether to consume single-use tokens after retrieval.
   * Set to `false` to check for token existence without side-effects.
   * @default true
   */
  consume?: boolean;
  scope: string;
};

/**
 * Gets an elevated access token by scope.
 *
 * This function retrieves an elevated access token that contains the specified scope.
 * Expired tokens are automatically filtered out.
 *
 * By default, if the token has `singleUse: true`, it will be automatically
 * consumed (removed from state) after retrieval. Pass `consume: false` to
 * check for token existence without consuming it.
 *
 * @param params - The parameters object.
 * @param params.scope - The scope to match (e.g., 'wallet:export').
 * @param params.consume - Whether to consume single-use tokens (default: true).
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns The elevated access token if found and not expired, or undefined if not found or expired.
 *
 * @example
 * ```typescript
 * // Retrieve and consume (default)
 * const token = getElevatedAccessToken({ scope: 'wallet:export' });
 *
 * // Check existence without consuming
 * const token = getElevatedAccessToken({ scope: 'credential:unlink', consume: false });
 * ```
  * @notInstrumented
 */
export const getElevatedAccessToken = (
  { consume = true, scope }: GetElevatedAccessTokenParams,
  client = getDefaultClient()
): string | undefined => {
  const core = getCore(client);

  const now = new Date();
  const elevatedAccessTokens = core.state.get().elevatedAccessTokens || [];

  // Filter out expired tokens
  const validTokens = elevatedAccessTokens.filter(
    (token) => !token.expiresAt || token.expiresAt > now
  );

  // Remove expired tokens from state if any were found
  if (validTokens.length !== elevatedAccessTokens.length) {
    core.state.set({
      elevatedAccessTokens: validTokens,
    });
  }

  const token = validTokens.find((token) => token.scopes.includes(scope));

  if (!token) {
    return undefined;
  }

  // Check if token should be consumed based on singleUse property
  if (consume && token.singleUse) {
    const updatedTokens = validTokens.filter((t) => t !== token);
    core.state.set({
      elevatedAccessTokens: updatedTokens,
    });
  }

  return token.token;
};
